import dayjs from 'dayjs'
import Decimal from 'decimal.js'
import isNil from 'lodash/isNil'
import { CostCalculationMethod, ProjectCostData } from 'types'

export const REPAIR_COST_PER_SQFT = 1000

export const REPAIR_SETUP_COST = 1000

export interface OpportunityCostProps {
  projectDate: dayjs.Dayjs
  discountPercentage: number
  startDate?: dayjs.Dayjs
}

interface SetupAndMaterialCostProps {
  materialArea: number
  materialCostPerSqFt?: number
  setupCost?: number
}

interface TotalCostEstimationProps {
  totalCost: number | null
}

type BaseCostEstimationProps<T extends CostCalculationMethod> = {
  costCalculationMethod: T
  opportunityCostProps?: OpportunityCostProps
}

type ProjectCostEstimationArgs<T extends CostCalculationMethod> = T extends
  | CostCalculationMethod.FEP_LEVEL_1
  | CostCalculationMethod.SETUP_AND_MATERIAL
  ? BaseCostEstimationProps<T> & SetupAndMaterialCostProps
  : T extends CostCalculationMethod.SIMPLE
    ? BaseCostEstimationProps<T> & TotalCostEstimationProps
    : never

export function calculateMaterialCost({
  materialArea,
  materialCostPerSqFt = REPAIR_COST_PER_SQFT,
}: {
  materialArea: number
  materialCostPerSqFt?: number
}): number {
  return new Decimal(materialArea).mul(materialCostPerSqFt).toNumber()
}

export function calculateLaborCost({
  materialArea,
  materialCostPerSqFt,
  setupCost,
}: {
  materialArea: number
  materialCostPerSqFt: number
  setupCost: number
}): number {
  const materialCost = new Decimal(
    calculateMaterialCost({ materialArea, materialCostPerSqFt })
  )
  return materialCost.add(setupCost).mul(2).toNumber()
}

export function calculateOverheadCost({
  materialArea,
  materialCostPerSqFt,
  setupCost,
}: {
  materialArea: number
  materialCostPerSqFt: number
  setupCost: number
}): number {
  const materialCost = new Decimal(
    calculateMaterialCost({ materialArea, materialCostPerSqFt })
  )
  return materialCost.add(setupCost).mul(0.5).toNumber()
}

/**
 *
 * @param projectDate - The date of the project
 * @param discountRate - The net yearly discount rate for the project
 * @param grossCost - The gross cost of the project
 * @param startDate - The start date for the calculation, defaults to the current date
 * @returns The amount discounted from the gross cost
 */
export function calculateOpporunityCostDiscount({
  projectDate,
  discountPercentage,
  grossCost,
  startDate,
}: { grossCost: number } & OpportunityCostProps): number | null {
  const yearsToProject = projectDate.diff(startDate ?? dayjs(), 'year', true)
  if (
    discountPercentage < -100 ||
    discountPercentage > 100 ||
    yearsToProject < 0
  ) {
    return null
  }

  const grossCostDec = new Decimal(grossCost)
  const totalDiscountRate = new Decimal(100 - discountPercentage)
    .div(new Decimal(100))
    .pow(yearsToProject)
  const netCost = grossCostDec.mul(totalDiscountRate)

  return grossCostDec.sub(netCost).toNumber()
}

/**
 * Calculate an estimation of the cost of a repair plan based on a Front-End Planning Level 1 Calculation
 * @param materialArea - The area of material required
 * @param materialCostPerSqFt - The material cost per square foot of the repair plan
 * @param setupCost - The equipment cost of the repair plan
 * @returns Estimation of total repair plan cost
 */
export function calculateFEPLevel1CostEstimation({
  materialArea,
  materialCostPerSqFt = REPAIR_COST_PER_SQFT,
  setupCost = REPAIR_SETUP_COST,
}: SetupAndMaterialCostProps): number | null {
  if (isNil(materialCostPerSqFt) || isNil(setupCost)) {
    return null
  }

  const materialCost = new Decimal(
    calculateMaterialCost({ materialArea, materialCostPerSqFt })
  )
  return materialCost.add(setupCost).mul(3.5).toNumber()
}

/**
 * Calculates the total cost estimation for setup and material.
 *
 * @param {number} [materialArea] - The area of the material in square feet.
 * @param {number} [materialCostPerSqFt=REPAIR_COST_PER_SQFT] - The cost of material per square foot. Defaults to REPAIR_COST_PER_SQFT.
 * @param {number} [setupCost=REPAIR_SETUP_COST] - The setup cost. Defaults to REPAIR_SETUP_COST.
 * @returns {number | null} - The total cost estimation or null if materialCostPerSqFt or setupCost is null or undefined.
 */
export function calculateSetupAndMaterialCostEstimation({
  materialArea,
  materialCostPerSqFt = REPAIR_COST_PER_SQFT,
  setupCost = REPAIR_SETUP_COST,
}: SetupAndMaterialCostProps): number | null {
  if (isNil(materialCostPerSqFt) || isNil(setupCost)) {
    return null
  }

  const materialCost = new Decimal(
    calculateMaterialCost({ materialArea, materialCostPerSqFt })
  )
  return materialCost.add(setupCost).toNumber()
}

/**
 * Calculate an estimation of the cost of a project, depending ont the calculation method provided
 * @param costCalculationMethod - The method of calculating the cost
 * @param opportunityCostProps - The properties for calculating the opportunity cost discount
 * @returns Estimation of total project cost
 */
export function calculateProjectCostEstimation<T extends CostCalculationMethod>(
  props: ProjectCostEstimationArgs<T>
): number | null {
  const getTotalCost = () => {
    switch (props.costCalculationMethod) {
      case CostCalculationMethod.FEP_LEVEL_1:
        return calculateFEPLevel1CostEstimation({
          materialArea: props.materialArea,
          materialCostPerSqFt: props.materialCostPerSqFt,
          setupCost: props.setupCost,
        })
      case CostCalculationMethod.SETUP_AND_MATERIAL:
        return calculateSetupAndMaterialCostEstimation(props)
      case CostCalculationMethod.SIMPLE:
        return props.totalCost
      default:
        return null
    }
  }

  const totalCost = getTotalCost()
  if (isNil(totalCost)) {
    return null
  }

  if (props.opportunityCostProps) {
    const discountAmount = calculateOpporunityCostDiscount({
      grossCost: totalCost,
      ...props.opportunityCostProps,
    })

    if (!isNil(discountAmount)) {
      return totalCost - discountAmount
    }
  }

  return totalCost
}

/**
 * Used to save project cost args to Rolodex entries
 */
export function getProjectCostArgs({
  costMethod,
  costPerSqFt,
  setupCost,
  customTotalCost,
}: Partial<ProjectCostData>): ProjectCostData | undefined {
  if (!costMethod) {
    return undefined
  }

  if (costMethod === CostCalculationMethod.SIMPLE) {
    return {
      costMethod,
      customTotalCost,
    }
  } else {
    return {
      costMethod,
      costPerSqFt,
      setupCost,
    }
  }
}

/**
 * Use material area and the props from the ProjectCostData to calculate the total cost of a project
 */
export function getTotalCostFromProjectCostData({
  costMethod,
  costPerSqFt,
  setupCost,
  customTotalCost,
  area,
  opportunityCostProps,
}: Partial<ProjectCostData> & {
  area?: number
  opportunityCostProps?: OpportunityCostProps
}): number | null {
  // For backwards compatibility, if the cost method is not set, infer it
  if (!costMethod) {
    if (customTotalCost) {
      costMethod = CostCalculationMethod.SIMPLE
    } else if (costPerSqFt && setupCost) {
      costMethod = CostCalculationMethod.SETUP_AND_MATERIAL
    } else {
      return null
    }
  }

  if (costMethod === CostCalculationMethod.SIMPLE) {
    return calculateProjectCostEstimation({
      costCalculationMethod: costMethod,
      totalCost: customTotalCost ?? null,
      opportunityCostProps,
    })
  } else {
    return area
      ? calculateProjectCostEstimation({
          costCalculationMethod: costMethod,
          materialArea: area,
          materialCostPerSqFt: costPerSqFt,
          setupCost: setupCost,
          opportunityCostProps,
        })
      : null
  }
}

export function projectCostEstimationReducer(
  state: ProjectCostData,
  action: Partial<ProjectCostData>
): ProjectCostData {
  return {
    ...state,
    ...action,
  }
}
